import { passwordStrength } from 'check-password-strength';
import { z } from 'zod';

export enum PasswordStrength {
  TooWeak,
  Weak,
  Medium,
  Strong,
}
interface IPasswordStrengthResult {
  id: PasswordStrength;
  value: string;
  length: number;
}
// TODO: Switch to zxcvbn for password strength
export const getPasswordStrength = (password: string): IPasswordStrengthResult => {
  if (!passwordValidationSchema.safeParse(password).success) {
    return {
      id: PasswordStrength.TooWeak,
      value: 'Too weak',
      length: password.length,
    };
  }
  return passwordStrength(password);
};

/**
 * Single source of truth for the client-side password policy, shared by the
 * validation schemas and the live checklist in `LiliPasswordInput`.
 */
export const PASSWORD_RULES = {
  minLength: 8,
  uppercase: /[A-Z]/,
  lowercase: /[a-z]/,
  number: /[0-9]/,
  // Any non-alphanumeric, non-whitespace character (a leading/trailing space is not a symbol).
  specialCharacter: /[^A-Za-z0-9\s]/,
} as const;

/** A flow's password policy. Everything not set falls back to `PASSWORD_RULES`. */
export interface PasswordPolicy {
  minLength?: number;
  requireSpecialCharacter?: boolean;
}

/**
 * Builds the validation schema for a flow's password policy. Flows whose backend requires a
 * symbol or a longer password (e.g. the partner portal) must build their schema from the same
 * values they hand to `LiliPasswordInput` (`minLength` + the checklist texts), so the hint and
 * the client-side validation never drift from the server policy.
 */
export const createPasswordValidationSchema = ({
  minLength = PASSWORD_RULES.minLength,
  requireSpecialCharacter = false,
}: PasswordPolicy = {}) => {
  const schema = z
    .string()
    .min(minLength)
    .regex(PASSWORD_RULES.uppercase)
    .regex(PASSWORD_RULES.lowercase)
    .regex(PASSWORD_RULES.number);

  return requireSpecialCharacter ? schema.regex(PASSWORD_RULES.specialCharacter) : schema;
};

export const passwordValidationSchema = createPasswordValidationSchema();

// Export the type for TypeScript consumers
export type PasswordValidationType = z.infer<typeof passwordValidationSchema>;
